import { useState, useEffect } from "react";
import { useAuth, SignIn, useUser } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, XCircle, Clock, MapPin, Loader2 } from "lucide-react";
import { client } from "@/client";
import { formatDateTime } from "@/lib/format-time";

interface CheckinPageProps {
  token: string;
}

interface EventInfo {
  id: number;
  title: string;
  description?: string;
  location?: string;
  startTime: string;
  endTime: string;
  points: number;
}

interface QrValidationResult {
  valid: boolean;
  error?: string;
  event?: EventInfo;
  checkInStartsAt?: string;
  eventEndedAt?: string;
}

interface CheckinResult {
  success: boolean;
  error?: string;
  pointsEarned?: number;
  event?: { id: number; title: string };
  checkIn?: { checkedInAt: string };
}

export function CheckinPage({ token }: CheckinPageProps) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const [validationResult, setValidationResult] = useState<QrValidationResult | null>(null);
  const [checkinResult, setCheckinResult] = useState<CheckinResult | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [isCheckingIn, setIsCheckingIn] = useState(false);

  // QR 코드 토큰을 세션에 저장 (회원가입 후 자동 체크인용)
  useEffect(() => {
    if (token) {
      sessionStorage.setItem("pendingCheckinToken", token);
    }
  }, [token]);

  // QR 코드 유효성 검증
  useEffect(() => {
    async function validateQr() {
      try {
        const res = await client.api["qr-codes"][":token"].$get({
          param: { token },
        });
        const data = (await res.json()) as QrValidationResult;
        setValidationResult(data);
      } catch {
        setValidationResult({ valid: false, error: "Failed to validate QR code" });
      } finally {
        setIsValidating(false);
      }
    }

    validateQr();
  }, [token]);

  // 로그인 후 자동 체크인 시도
  useEffect(() => {
    const pendingToken = sessionStorage.getItem("pendingCheckinToken");
    if (isSignedIn && pendingToken && validationResult?.valid && !checkinResult) {
      handleCheckin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, validationResult]);

  // 체크인 처리 (with retry for webhook delay)
  async function handleCheckin(retryCount = 0) {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2000;

    if (!isSignedIn || isCheckingIn) return;

    setIsCheckingIn(true);
    try {
      const authToken = await getToken();
      const res = await client.api["check-ins"].$post(
        { json: { token } },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      const data = (await res.json()) as CheckinResult & { error?: string };

      // 401 에러 + User not found = webhook 아직 미도착 → 재시도
      // Note: authMiddleware returns 401, but Hono RPC types only include route-defined statuses
      if ((res.status as number) === 401 && data.error === "User not found in database" && retryCount < MAX_RETRIES) {
        setIsCheckingIn(false);
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        return handleCheckin(retryCount + 1);
      }

      setCheckinResult(data);

      // 체크인 성공 시 세션에서 토큰 제거
      if (data.success) {
        sessionStorage.removeItem("pendingCheckinToken");
      }
    } catch {
      setCheckinResult({ success: false, error: "Failed to check in" });
    } finally {
      setIsCheckingIn(false);
    }
  }

  // 로딩 중
  if (!isLoaded || isValidating) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center py-8">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">확인 중...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // QR 코드 무효
  if (!validationResult?.valid) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <CardTitle>체크인 불가</CardTitle>
            <CardDescription>
              {validationResult?.error === "Check-in not yet available"
                ? `체크인은 ${formatDateTime(validationResult.checkInStartsAt)}부터 가능합니다.`
                : validationResult?.error === "Check-in period has ended"
                ? "체크인 시간이 종료되었습니다."
                : validationResult?.error ?? "유효하지 않은 QR 코드입니다."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const event = validationResult.event!;

  // 체크인 완료
  if (checkinResult?.success) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <CardTitle>체크인 완료!</CardTitle>
            <CardDescription className="text-lg">
              {event.title}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-primary">
                +{checkinResult.pointsEarned} 포인트
              </p>
              <p className="text-sm text-muted-foreground">
                적립되었습니다
              </p>
            </div>
            <div className="text-center text-sm text-muted-foreground">
              {user?.firstName}님, 오늘도 함께해 주셔서 감사합니다!
            </div>
            <Button className="w-full" onClick={() => window.location.href = "/app"}>
              대시보드로 이동
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 체크인 실패 (중복 등)
  if (checkinResult && !checkinResult.success) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <XCircle className="h-16 w-16 text-orange-500 mx-auto mb-4" />
            <CardTitle>이미 체크인하셨습니다</CardTitle>
            <CardDescription>{event.title}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => window.location.href = "/app"}>
              대시보드로 이동
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // 로그인 필요
  if (!isSignedIn) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>{event.title}</CardTitle>
            <CardDescription>체크인하려면 로그인이 필요합니다</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{formatDateTime(event.startTime)} - {formatDateTime(event.endTime)}</span>
            </div>
            {event.location && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                <span>{event.location}</span>
              </div>
            )}
            <div className="bg-muted rounded-lg p-3 text-center">
              <span className="text-primary font-semibold">+{event.points} 포인트</span>
              <span className="text-muted-foreground"> 적립 예정</span>
            </div>
            <SignIn routing="hash" afterSignInUrl={`/checkin/${token}`} />
          </CardContent>
        </Card>
      </div>
    );
  }

  // 체크인 대기 중
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>{event.title}</CardTitle>
          {event.description && (
            <CardDescription>{event.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{formatDateTime(event.startTime)} - {formatDateTime(event.endTime)}</span>
          </div>
          {event.location && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>{event.location}</span>
            </div>
          )}
          <div className="bg-muted rounded-lg p-3 text-center">
            <span className="text-primary font-semibold">+{event.points} 포인트</span>
            <span className="text-muted-foreground"> 적립 예정</span>
          </div>
          <Button
            className="w-full"
            size="lg"
            onClick={() => handleCheckin()}
            disabled={isCheckingIn}
          >
            {isCheckingIn ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                체크인 중...
              </>
            ) : (
              "체크인하기"
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
